"use client";
import { useEffect, useState, Suspense } from "react";
import dynamic from "next/dynamic";
import Image from "next/image";
import { NewNavBar } from "../common/NewNavBar";
import Hero from "./Hero";
import HomePageNotFound from "./HomePageNotFound";
import type { HomePageDetails } from "@/types/homepage";
import { useAuth } from "@/hook/useAuth";
import { sendCustomGaEvent } from "@/scripts/ga";

// Above the fold - keep static for LCP
import SearchSmart from "./SearchSmart";

// Below the fold - import dynamically to reduce initial bundle size and split chunks
const TopMatches = dynamic(() => import("./TopMatches"), { ssr: true });
const GlobePath = dynamic(() => import("./GlobePath"), { ssr: true });
const TrendingPrograms = dynamic(() => import("./TrendingPrograms"), { ssr: true });
const AiMentor = dynamic(() => import("./AiMentor"), { ssr: true });
const ScholarChallenge = dynamic(() => import("../homepage/ScholarChallenge"), { ssr: true });
const StudentStories = dynamic(() => import("./StudentStories"), { ssr: true });
const Guide = dynamic(() => import("./Guide"), { ssr: true });
const AsFeatured = dynamic(() => import("./AsFeatured"), { ssr: true });
const Blogs = dynamic(() => import("./Blogs"), { ssr: true });
const PromiseSection = dynamic(() => import("./Promise"), { ssr: true });
const HomePageFAQ = dynamic(() => import("./HomePageFAQ"), { ssr: true });
const Footer = dynamic(() => import("../common/Footer"), { ssr: true });
const TalkToExperts = dynamic(() => import("../homepage/TalkToExperts"), { ssr: false });

type NewHomepageProps = {
  homePageDetails?: HomePageDetails | null;
};

// Reusable elegant skeleton skeleton loader for deferred components
const SkeletonSection = () => (
  <div className="container 2xl:max-w-[1628px] mx-auto py-12 px-4 animate-pulse">
    <div className="h-8 bg-gray-200 dark:bg-gray-800 rounded w-1/4 mb-6"></div>
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
      <div className="h-[250px] bg-gray-200 dark:bg-gray-800 rounded-2xl"></div>
      <div className="h-[250px] bg-gray-200 dark:bg-gray-800 rounded-2xl"></div>
      <div className="h-[250px] bg-gray-200 dark:bg-gray-800 rounded-2xl"></div>
    </div>
  </div>
);

const NewHomepage = ({ homePageDetails }: NewHomepageProps) => {
  const { isAnonymous } = useAuth();
  const [expertForm, setExpertForm] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);

  const expertFromHandler = () => {
    void sendCustomGaEvent("homepage_talk_to_experts_open_click", {
      is_anonymous: isAnonymous,
    });
    setExpertForm(true);
  };

  useEffect(() => {
    setCurrentStep(isAnonymous ? 1 : 2);
  }, [expertForm, isAnonymous]);

  useEffect(() => {
    void sendCustomGaEvent("homepage_view", {
      is_anonymous: isAnonymous,
    });
  }, [isAnonymous]);

  return (
    <>
      {!homePageDetails ? (
        <HomePageNotFound />
      ) : (
        <>
          <main id="main-content">
            <div className="md:min-h-screen min-h-[400px] relative flex items-center overflow-hidden">
              <div className="absolute inset-0 -z-10 hidden sm:block bg-[#03091e]">
                <Image
                  src="/assets/images/png/homepage/heroImage.webp"
                  alt="Hero Background"
                  fill
                  priority
                  fetchPriority="high"
                  sizes="(min-width: 640px) 100vw, 0px"
                  className="object-cover object-center"
                />
              </div>
              <NewNavBar />
              <Hero />
            </div>
            <SearchSmart content={homePageDetails?.globalSearchCards} />

            <Suspense fallback={<SkeletonSection />}>
              <TopMatches
                content={homePageDetails?.mentorsCards}
                labe={homePageDetails?.mentorsLabels}
              />
            </Suspense>

            <Suspense fallback={<SkeletonSection />}>
              <GlobePath />
            </Suspense>

            <Suspense fallback={<SkeletonSection />}>
              <TrendingPrograms />
            </Suspense>

            <Suspense fallback={<SkeletonSection />}>
              <AiMentor />
            </Suspense>

            <Suspense fallback={<SkeletonSection />}>
              <ScholarChallenge />
            </Suspense>

            <Suspense fallback={<SkeletonSection />}>
              <StudentStories />
            </Suspense>

            <Suspense fallback={<SkeletonSection />}>
              <Guide />
            </Suspense>

            <Suspense fallback={<SkeletonSection />}>
              <AsFeatured
                expertFromHandler={expertFromHandler}
                content={homePageDetails?.media}
              />
            </Suspense>

            <Suspense fallback={<SkeletonSection />}>
              <Blogs />
            </Suspense>

            <Suspense fallback={<SkeletonSection />}>
              <PromiseSection />
            </Suspense>

            <Suspense fallback={<SkeletonSection />}>
              <HomePageFAQ content={homePageDetails?.faq} />
            </Suspense>
          </main>

          <Suspense fallback={<SkeletonSection />}>
            <Footer />
          </Suspense>

          {expertForm && (
            <Suspense fallback={null}>
              <TalkToExperts
                expertForm={expertForm}
                setExpertForm={setExpertForm}
                currentStep={currentStep}
                setCurrentStep={setCurrentStep}
              />
            </Suspense>
          )}
        </>
      )}
    </>
  );
};
export default NewHomepage;
